import { CenteredDialog } from '../../components/ui/centered-dialog';
import React, { useEffect, useState } from 'react';
import {
  Layers,
  Wrench,
  UserCheck,
  AlertOctagon,
  AlertTriangle,
  Ban,
  Eye,
  Ruler,
  Plus,
  Search,
  CheckCircle2,
  Slash,
  Clock,
  Edit2,
  PowerOff,
  RefreshCw,
  Building2,
  Flame,
  CloudOff,
  UploadCloud,
  KeyRound,
  Scale,
} from 'lucide-react';
import type { CelulaKanbanEntity, ClienteEntity, EstadoIntegracaoOmie, EstufaEntity } from '@pieltec/contracts';
import { ESTADO_INTEGRACAO_OMIE_LABEL } from '@pieltec/contracts';
import { apiMutate } from '../../lib/csrf-fetch';
import { obterSessaoAtual, type ObterSessaoResult } from '../../lib/session';
import {
  deveExibirFinanceiro,
  formatarDocumentoFiscal,
  resolverBadgeFinanceiro,
} from '../../lib/financeiro-badge';
import { ClienteDetalheSheet } from '../../components/cadastros/cliente-detalhe-sheet';
import { CadastroForm, type CadastroFieldConfig } from './cadastro-form';
import { CelulaTurnoCalendarioModal } from './celula-turno-calendario-form';
import { CelulaKanbanForm } from './celula-kanban-form';
import { EstufaForm } from './estufa-form';
import { rotulosCadastro } from '../../lib/cadastros/rotulos-cadastro';
import { montarPayloadCadastro } from '../../lib/cadastros/payload-cadastro';
import { lerDeepLinkCadastro } from '../../lib/kanban/cadastro-kanban';
import { TablePagination } from '../../components/ui/table-pagination';
import { useClientPagination } from '../../hooks/use-client-pagination';
import { CampoSelect } from '../../components/ui/campo-select';

type EntidadeTipo =
  | 'clientes'
  | 'celulas'
  | 'estufas'
  | 'servicos'
  | 'recursos'
  | 'operadores'
  | 'tipos-interrupcao'
  | 'unidades-medida';

interface EntidadeMeta {
  key: EntidadeTipo;
  singular: string;
  /** Gênero do cadastro: define 'Nova/Novo' e 'Nenhuma/Nenhum' nos textos. */
  genero: 'f' | 'm';
  plural: string;
  icon: React.ComponentType<{ className?: string }>;
  endpoint: string;
  fields: CadastroFieldConfig[];
  /** Nome do query param do filtro de ativos: a rota de celulas le `apenasAtivas`. */
  filtroAtivosParam?: 'apenasAtivos' | 'apenasAtivas';
}

interface CadastroListaItem {
  readonly id: string;
  readonly codigo?: string;
  readonly sigla?: string;
  readonly nome: string;
  readonly nomeFantasia?: string;
  readonly documentoFiscal?: string;
  readonly ordemPadrao?: number;
  readonly apareceNoKanban?: boolean;
  readonly servicoNome?: string;
  readonly ehCabine?: boolean;
  readonly ativo: boolean;
}

function ehCadastroListaItem(valor: unknown): valor is CadastroListaItem {
  if (typeof valor !== 'object' || valor === null) return false;
  const item = valor as Record<string, unknown>;
  return typeof item.id === 'string' && typeof item.nome === 'string' && typeof item.ativo === 'boolean';
}

function itensDaResposta(valor: unknown): CadastroListaItem[] {
  if (typeof valor !== 'object' || valor === null) return [];
  const dados = (valor as Record<string, unknown>).data;
  return Array.isArray(dados) ? dados.filter(ehCadastroListaItem) : [];
}

function mensagemDeErro(valor: unknown, padrao: string): string {
  if (typeof valor !== 'object' || valor === null) return padrao;
  const mensagem = (valor as Record<string, unknown>).message;
  if (typeof mensagem === 'string') return mensagem;
  if (Array.isArray(mensagem) && mensagem.every((item) => typeof item === 'string')) return mensagem.join(' ');
  return padrao;
}

const ENTIDADES: EntidadeMeta[] = [
  {
    key: 'clientes',
    genero: 'm',
    singular: 'Cliente',
    plural: 'Clientes',
    icon: Building2,
    endpoint: '/api/v1/cadastros/clientes',
    fields: [
      { name: 'codigo', label: 'Código do Cliente', placeholder: 'Ex: CLI-001', required: true, apenasNaCriacao: true },
      { name: 'nome', label: 'Razão Social / Nome Fantasia', placeholder: 'Ex: Metalúrgica Exemplo', required: true },
    ],
  },
  {
    key: 'celulas',
    genero: 'f',
    singular: 'Célula de Produção',
    plural: 'Células de Produção',
    icon: Layers,
    endpoint: '/api/v1/planejamento/celulas',
    filtroAtivosParam: 'apenasAtivas',
    fields: [
      { name: 'codigo', label: 'Código da Célula', placeholder: 'Ex: CEL-BANHO-01', required: true, apenasNaCriacao: true },
      {
        name: 'nome',
        label: 'Nome da Célula',
        placeholder: 'Ex: Linha de Eletrodeposição',
        required: true,
        requiredMessage: 'Informe o nome da célula.',
      },
      {
        name: 'ativo',
        label: 'Ativa',
        type: 'select',
        valueType: 'boolean',
        apenasNaCriacao: true,
        placeholder: 'Não = cabine em implantação, fora do quadro',
        options: [
          { label: 'Sim', value: 'true' },
          { label: 'Não', value: 'false' },
        ],
      },
    ],
  },
  {
    key: 'estufas',
    genero: 'f',
    singular: 'Estufa',
    plural: 'Estufas',
    icon: Flame,
    endpoint: '/api/v1/planejamento/estufas',
    filtroAtivosParam: 'apenasAtivas',
    // O cadastro abre o EstufaForm (cabines e limites); estes campos servem à busca e à lista.
    fields: [
      { name: 'codigo', label: 'Código da Estufa', placeholder: 'Ex.: E', required: true, apenasNaCriacao: true },
      { name: 'nome', label: 'Nome da Estufa', placeholder: 'Ex.: Estufa E', required: true },
    ],
  },
  {
    key: 'servicos',
    genero: 'm',
    singular: 'Serviço / Etapa',
    plural: 'Serviços / Etapas',
    icon: Wrench,
    endpoint: '/api/v1/cadastros/servicos',
    fields: [
      { name: 'codigo', label: 'Código do Serviço', placeholder: 'Ex: SRV-ZINCO', required: true, apenasNaCriacao: true },
      { name: 'nome', label: 'Nome da Etapa de Roteiro', placeholder: 'Ex: Banho de Zinco Eletrolítico', required: true },
      { name: 'ordemPadrao', label: 'Ordem Padrão no Roteiro', type: 'number', valueType: 'number', placeholder: '1', required: true },
      { name: 'apareceNoKanban', label: 'Aparece no Kanban', type: 'select', valueType: 'boolean', options: [{ label: 'Sim', value: 'true' }, { label: 'Não', value: 'false' }] },
    ],
  },
  {
    key: 'recursos',
    genero: 'm',
    singular: 'Recurso / Equipamento',
    plural: 'Recursos / Equipamentos',
    icon: Wrench,
    endpoint: '/api/v1/cadastros/recursos',
    fields: [
      { name: 'codigo', label: 'Código do Recurso', placeholder: 'Ex: REC-TQ01', required: true, apenasNaCriacao: true },
      { name: 'nome', label: 'Nome do Recurso', placeholder: 'Ex: Tanque de Banho 1', required: true },
    ],
  },
  {
    key: 'operadores',
    genero: 'm',
    singular: 'Operador',
    plural: 'Operadores',
    icon: UserCheck,
    endpoint: '/api/v1/cadastros/operadores',
    fields: [
      { name: 'codigo', label: 'Matrícula / Código', placeholder: 'Ex: OP-042', required: true, apenasNaCriacao: true },
      { name: 'nome', label: 'Nome Completo', placeholder: 'Ex: Carlos Alberto Silva', required: true },
    ],
  },
  {
    key: 'tipos-interrupcao',
    genero: 'm',
    singular: 'Tipo de Interrupção',
    plural: 'Tipos de Interrupção',
    icon: AlertOctagon,
    endpoint: '/api/v1/cadastros/tipos-interrupcao',
    fields: [
      { name: 'codigo', label: 'Código da Interrupção', placeholder: 'Ex: INT-MANUT', required: true, apenasNaCriacao: true },
      { name: 'nome', label: 'Descrição da Parada', placeholder: 'Ex: Manutenção Corretiva', required: true },
    ],
  },
  {
    key: 'unidades-medida',
    genero: 'f',
    singular: 'Unidade de Medida',
    plural: 'Unidades de Medida',
    icon: Ruler,
    endpoint: '/api/v1/cadastros/unidades-medida',
    fields: [
      { name: 'sigla', label: 'Sigla da Unidade', placeholder: 'Ex: kg, m, pç', required: true, apenasNaCriacao: true },
      { name: 'nome', label: 'Nome da Unidade', placeholder: 'Ex: Quilograma', required: true },
    ],
  },
];

// D-03/D-07 (Fase 09, Plano 06): ícone + rótulo textual exato para cada um dos quatro
// estados de integração — nunca cor isolada (NFR-08). Ordem alinhada a
// ESTADO_INTEGRACAO_OMIE_LABEL (contrato compartilhado, fonte única do texto exato).
const OMIE_ESTADO_ICON: Record<EstadoIntegracaoOmie, React.ComponentType<{ className?: string }>> = {
  SOMENTE_LOCAL: CloudOff,
  PENDENTE: Clock,
  SINCRONIZADO: CheckCircle2,
  FALHA_SINCRONIZACAO: AlertTriangle,
  CONFLITO: Scale,
};

const OMIE_ESTADO_TOM: Record<EstadoIntegracaoOmie, string> = {
  SOMENTE_LOCAL: 'bg-slate-100 text-slate-600 border-slate-200',
  PENDENTE: 'bg-amber-50 text-amber-700 border-amber-200',
  SINCRONIZADO: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  FALHA_SINCRONIZACAO: 'bg-rose-50 text-rose-700 border-rose-200',
  CONFLITO: 'bg-rose-50 text-rose-800 border-rose-300',
};

export const CadastroListPage: React.FC = () => {
  // D-45: o atalho do cabecalho da raia abre /cadastros?aba=celulas&celula=CODIGO. Lido uma
  // unica vez ao montar; a aba ja nasce correta para nao buscar Clientes antes.
  const [deepLink] = useState(() =>
    typeof window !== 'undefined'
      ? lerDeepLinkCadastro(window.location.search)
      : { aba: null, celulaCodigo: null },
  );
  const [activeTab, setActiveTab] = useState<EntidadeTipo>(() =>
    ENTIDADES.some((e) => e.key === deepLink.aba) ? (deepLink.aba as EntidadeTipo) : 'clientes',
  );
  const [celulaPendente, setCelulaPendente] = useState<string | null>(deepLink.celulaCodigo);
  const [itensDaAba, setItensDaAba] = useState<EntidadeTipo | null>(null);
  const [avisoDeepLink, setAvisoDeepLink] = useState<string | null>(null);
  const [itens, setItens] = useState<CadastroListaItem[]>([]);
  const [apenasAtivos, setApenasAtivos] = useState(true);
  const [busca, setBusca] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [itemEdicao, setItemEdicao] = useState<CadastroListaItem | null>(null);
  // Cada abertura do formulário monta um formulário novo (key), sem estado da abertura anterior.
  const [aberturaFormulario, setAberturaFormulario] = useState(0);
  const [itemDesativacao, setItemDesativacao] = useState<CadastroListaItem | null>(null);
  const [celulaTurnoModal, setCelulaTurnoModal] = useState<{ id: string; nome: string; codigo: string } | null>(null);
  const [celulaKanbanModal, setCelulaKanbanModal] = useState<CelulaKanbanEntity | null>(null);
  const [clienteDetalhe, setClienteDetalhe] = useState<ClienteEntity | null>(null);
  const [detalheSheetOpen, setDetalheSheetOpen] = useState(false);
  const [sessao, setSessao] = useState<ObterSessaoResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [erroCarga, setErroCarga] = useState<string | null>(null);
  // D-03 (Fase 09, Plano 06): id do cliente recém-criado lido uma única vez da query
  // string ao montar; consumido assim que a linha aparece na lista carregada.
  const [novoClienteIdPendente, setNovoClienteIdPendente] = useState<string | null>(() =>
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('novoClienteId') : null,
  );
  const [novoClienteDestaque, setNovoClienteDestaque] = useState<string | null>(null);
  const [avisoNovoCliente, setAvisoNovoCliente] = useState<string | null>(null);
  // D-07/D-08: id do cliente com envio tardio em andamento — desabilita repetição e evita
  // duplicar POST /enviar-omie enquanto a requisição estiver pendente.
  const [enviandoOmieId, setEnviandoOmieId] = useState<string | null>(null);

  useEffect(() => {
    void obterSessaoAtual().then(setSessao);
  }, []);

  const currentMeta = ENTIDADES.find((e) => e.key === activeTab) ?? ENTIDADES[0]!;
  const exibeFinanceiro = activeTab === 'clientes' && deveExibirFinanceiro(sessao?.autenticado ? sessao.usuario : null);
  // D-01/D-08: mesma permissão que autoriza o backend (cadastros:gerenciar) controla a
  // visibilidade de "Novo cliente" e "Enviar para a Omie" — ergonomia de UI apenas; o
  // backend continua a autoridade de RBAC (403 em chamada forçada sem a permissão).
  const podeGerenciarCadastros = Boolean(
    sessao?.autenticado && sessao.usuario.permissions.includes('cadastros:gerenciar'),
  );
  // Fase 17 (D-10): atalho para os acessos do portal do cliente; a rota e a API repetem a checagem.
  const podeGerenciarAcessosPortal = Boolean(
    sessao?.autenticado && sessao.usuario.permissions.includes('portal:gerenciar-acessos'),
  );

  const carregarItens = async () => {
    setLoading(true);
    setErroCarga(null);
    try {
      const filtro = currentMeta.filtroAtivosParam ?? 'apenasAtivos';
      const res = await fetch(`${currentMeta.endpoint}?${filtro}=${apenasAtivos}`);
      if (res.ok) {
        const json: unknown = await res.json();
        setItens(itensDaResposta(json));
        setItensDaAba(currentMeta.key);
      } else {
        setItens([]);
        setErroCarga(`Não foi possível carregar ${currentMeta.plural.toLowerCase()}. Tente atualizar.`);
      }
    } catch {
      setItens([]);
      setErroCarga(`Não foi possível carregar ${currentMeta.plural.toLowerCase()}. Verifique a conexão.`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void carregarItens();
  }, [activeTab, apenasAtivos]);

  const handleSalvar = async (values: Record<string, unknown>) => {
    const isEdit = Boolean(itemEdicao);
    const url = isEdit && itemEdicao ? `${currentMeta.endpoint}/${itemEdicao.id}` : currentMeta.endpoint;
    const method = isEdit ? 'PATCH' : 'POST';
    // T-11-G08: so os campos declarados, com o tipo que a API espera (whitelist + forbidNonWhitelisted).
    const payload = montarPayloadCadastro(currentMeta.fields, values, isEdit ? 'editar' : 'criar');

    const res = await apiMutate(url, { method, body: payload });

    if (!res.ok) {
      const errorData: unknown = await res.json().catch(() => null);
      throw new Error(mensagemDeErro(errorData, `Erro ao salvar ${currentMeta.singular}`));
    }

    void carregarItens();
  };

  const handleDesativar = async () => {
    if (!itemDesativacao) return;

    try {
      const url = `${currentMeta.endpoint}/${itemDesativacao.id}/desativar`;
      const res = await apiMutate(url, { method: 'PATCH' });
      if (!res.ok) {
        // A API explica o motivo (ex.: 409 'Desvincule as cabines antes de desativar a estufa.').
        const corpo = (await res.json().catch(() => ({}))) as { message?: string | string[] };
        const motivo = Array.isArray(corpo.message) ? corpo.message.join(' ') : corpo.message;
        throw new Error(motivo || 'Falha ao desativar registro');
      }
      setItemDesativacao(null);
      void carregarItens();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Falha na desativação');
    }
  };

  // D-07 (Fase 09, Plano 04/06): envio tardio de um cliente SOMENTE_LOCAL/FALHA para a
  // Omie. Chama exclusivamente o endpoint de Cadastros (nunca a Omie diretamente do
  // browser); ao concluir, recarrega a lista para refletir o novo estado (PENDENTE).
  const handleEnviarOmie = async (item: ClienteEntity) => {
    const ok = window.confirm(
      'Enviar este cliente para a Omie agora?\n\n' +
        'Depois que a Omie confirmar o vínculo, ele se torna permanente e não poderá ser desfeito.',
    );
    if (!ok) return;

    setEnviandoOmieId(item.id);
    try {
      const res = await apiMutate(`/api/v1/cadastros/clientes/${item.id}/enviar-omie`, { method: 'POST' });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(data.message || 'Não foi possível enviar o cliente para a Omie.');
      }
      await carregarItens();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Falha ao enviar o cliente para a Omie.');
    } finally {
      setEnviandoOmieId(null);
    }
  };

  const itensFiltrados = itens.filter((item) => {
    const texto = `${item.codigo ?? item.sigla ?? ''} ${item.nome} ${item.nomeFantasia ?? ''} ${item.documentoFiscal ?? ''}`.toLowerCase();
    return texto.includes(busca.toLowerCase());
  });
  const pagination = useClientPagination(itensFiltrados, [activeTab, busca, apenasAtivos]);

  // D-03: assim que o cliente recém-criado aparece na lista carregada, garante que a
  // página exibida o contenha, aplica o destaque (texto/ícone/aria-live além da cor) e
  // limpa a query string — evita reaplicar o destaque num refresh posterior.
  useEffect(() => {
    if (activeTab !== 'clientes' || !novoClienteIdPendente) return;
    const idx = itensFiltrados.findIndex((item) => (item as ClienteEntity).id === novoClienteIdPendente);
    if (idx === -1) return;
    const targetPage = Math.floor(idx / pagination.pageSize) + 1;
    pagination.setPage(targetPage);
    setNovoClienteDestaque(novoClienteIdPendente);
    setAvisoNovoCliente('Cliente cadastrado com sucesso. A linha destacada abaixo mostra o novo registro.');
    window.history.replaceState(null, '', '/cadastros');
    setNovoClienteIdPendente(null);
  }, [itensFiltrados, activeTab]);

  // D-45 / T-11-G09: o codigo do link so e comparado com as celulas ja carregadas pela sessao;
  // nenhuma escrita parte do link. Achou: abre o formulario Kanban da celula.
  useEffect(() => {
    if (!celulaPendente || activeTab !== 'celulas' || itensDaAba !== 'celulas' || loading) return;
    const alvo = itens.find((item) => item.codigo === celulaPendente);
    if (alvo) {
      setCelulaKanbanModal(alvo as CelulaKanbanEntity);
    } else {
      setAvisoDeepLink(`Célula ${celulaPendente} não encontrada.`);
    }
    window.history.replaceState(null, '', '/cadastros');
    setCelulaPendente(null);
  }, [celulaPendente, activeTab, itensDaAba, itens, loading]);

  // Foco gerenciado (D-03/NFR-08): move o foco para a linha destacada assim que ela
  // existir no DOM, sem depender de hover nem de cor isolada para ser percebida.
  useEffect(() => {
    if (!novoClienteDestaque) return;
    document.getElementById(`cliente-row-${novoClienteDestaque}`)?.focus();
  }, [novoClienteDestaque]);

  // Genero do cadastro: 'Nova Estufa', 'Nova Unidade de Medida', 'Novo Recurso / Equipamento'.
  const rotulos = rotulosCadastro(currentMeta);

  // Caminho unico para criar: cabecalho e estado vazio abrem o formulario do mesmo jeito (G-11-6).
  const abrirNovoCadastro = () => {
    setItemEdicao(null);
    setAberturaFormulario((n) => n + 1);
    setModalOpen(true);
  };

  const contagemTexto = `${itensFiltrados.length} ${
    itensFiltrados.length === 1 ? currentMeta.singular : currentMeta.plural
  }`;

  return (
    <div className="flex flex-col h-full space-y-4 p-4 max-w-7xl mx-auto">
      {/* Região de anúncio acessível para leitores de tela (D-03) */}
      <div aria-live="polite" className="sr-only">
        {avisoNovoCliente}
      </div>

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b pb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Cadastros da operação</h1>
          <p className="text-sm text-slate-500">
            Clientes, serviços, células, recursos e parâmetros da casa.
          </p>
        </div>

        {/* D-01/D-08 (Fase 09): Clientes tem entrada única para a página dedicada de
            cadastro completo — nunca reabre o modal genérico de criação rápida. Some
            inteiramente sem cadastros:gerenciar (ergonomia; o backend segue autoritativo). */}
        {activeTab === 'clientes' ? (
          podeGerenciarCadastros && (
            <a
              href="/cadastros/clientes/novo"
              className="flex items-center gap-1.5 px-4 py-2 bg-slate-900 text-white rounded-lg text-xs font-semibold hover:bg-slate-800 shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Novo cliente
            </a>
          )
        ) : (
          <button
            type="button"
            onClick={abrirNovoCadastro}
            className="flex items-center gap-1.5 px-4 py-2 bg-slate-900 text-white rounded-lg text-xs font-semibold hover:bg-slate-800 shadow-sm"
          >
            <Plus className="w-4 h-4" />
            {rotulos.novo}
          </button>
        )}
      </div>

      <div className="lg:hidden block text-sm"><span id="categoria-cadastro-rotulo">Categoria de cadastro</span><CampoSelect aria-labelledby="categoria-cadastro-rotulo" className="mt-2 data-[size=default]:h-12" value={activeTab} onValueChange={valor => { setActiveTab(valor as typeof activeTab); setBusca(''); }} options={ENTIDADES.map(ent => ({ value: ent.key, label: ent.plural }))} /></div>
      {/* Tabs de Entidades */}
      <div className="cadastro-categorias hidden lg:flex overflow-x-auto gap-2 border-b pb-2">
        {ENTIDADES.map((ent) => {
          const Icon = ent.icon;
          const isActive = ent.key === activeTab;
          return (
            <button
              key={ent.key}
              onClick={() => {
                setActiveTab(ent.key);
                setBusca('');
              }}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                isActive
                  ? 'bg-slate-900 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {ent.plural}
            </button>
          );
        })}
      </div>

      {/* Barra de Filtros e Busca */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-50 p-3 rounded-lg border">
        <div className="flex items-center gap-2 flex-1 max-w-md">
          <div className="relative w-full">
            <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" />
            <input
              type="text"
              placeholder={`Buscar por código ou nome em ${currentMeta.plural.toLowerCase()}...`}
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 bg-white border border-slate-300 rounded-md text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-900"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={apenasAtivos}
              onChange={(e) => setApenasAtivos(e.target.checked)}
              className="rounded border-slate-300 text-slate-900 focus:ring-slate-900"
            />
            <span>Apenas ativos</span>
          </label>

          <span className="text-xs font-semibold text-slate-500 font-mono">
            {loading ? 'Carregando…' : contagemTexto}
          </span>

          <button
            type="button"
            onClick={() => void carregarItens()}
            disabled={loading}
            className="flex items-center gap-1.5 px-2.5 py-1.5 border rounded-md hover:bg-white text-slate-700 text-xs font-semibold disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </div>

      {erroCarga && (
        <div className="p-3 rounded-lg text-xs bg-rose-50 text-rose-800 border border-rose-200" role="alert">
          {erroCarga}
        </div>
      )}

      {avisoDeepLink && (
        <div className="p-3 rounded-lg text-xs bg-amber-50 text-amber-800 border border-amber-200" role="status">
          {avisoDeepLink}
        </div>
      )}

      {/* Grade D5 */}
      <div className="border rounded-lg overflow-hidden bg-white shadow-xs">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Carregando {currentMeta.plural.toLowerCase()}…</div>
        ) : itensFiltrados.length === 0 ? (
          <div className="p-8 text-center space-y-3">
            <div className="mx-auto w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
              <currentMeta.icon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800">
                {erroCarga
                  ? `Não foi possível listar ${currentMeta.plural.toLowerCase()}.`
                  : rotulos.vazio}
              </p>
              <p className="text-xs text-slate-500">
                {erroCarga
                  ? 'Use Atualizar para tentar de novo.'
                  : 'Comece cadastrando o primeiro registro para a operação.'}
              </p>
            </div>
            {activeTab === 'clientes' ? (
              podeGerenciarCadastros && (
                <a
                  href="/cadastros/clientes/novo"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white rounded-md text-xs font-semibold hover:bg-slate-800"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Cadastrar Cliente
                </a>
              )
            ) : (
              <button
                type="button"
                onClick={abrirNovoCadastro}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white rounded-md text-xs font-semibold hover:bg-slate-800"
              >
                <Plus className="w-3.5 h-3.5" />
                Cadastrar {currentMeta.singular}
              </button>
            )}
          </div>
        ) : (
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b text-slate-600 font-semibold uppercase tracking-wider text-[11px]">
                <th className="py-2.5 px-3">Código / Sigla</th>
                <th className="py-2.5 px-3">Nome / Descrição</th>
                {activeTab === 'servicos' && <th className="py-2.5 px-3 text-center">Ordem Roteiro</th>}
                {activeTab === 'servicos' && <th className="py-2.5 px-3 text-center">Kanban</th>}
                {activeTab === 'celulas' && <th className="py-2.5 px-3">Processo</th>}
                {activeTab === 'celulas' && <th className="py-2.5 px-3">Cabine</th>}
                {activeTab === 'estufas' && <th className="py-2.5 px-3">Cabines</th>}
                {exibeFinanceiro && <th className="py-2.5 px-3 text-center">Financeiro</th>}
                {activeTab === 'clientes' && <th className="py-2.5 px-3 text-center">Integração Omie</th>}
                <th className="py-2.5 px-3 text-center">Status</th>
                <th className="py-2.5 px-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pagination.pageItems.map((item) => {
                const badgeFinanceiro = activeTab === 'clientes' ? resolverBadgeFinanceiro(item as ClienteEntity) : null;

                const destacado = activeTab === 'clientes' && (item as ClienteEntity).id === novoClienteDestaque;

                return (
                  <tr
                    key={item.id}
                    id={activeTab === 'clientes' ? `cliente-row-${(item as ClienteEntity).id}` : undefined}
                    tabIndex={activeTab === 'clientes' ? -1 : undefined}
                    className={`hover:bg-slate-50/80 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-slate-900/20 ${
                      destacado ? 'bg-emerald-50/70' : ''
                    }`}
                  >
                    <td className="py-2.5 px-3 font-mono font-medium text-slate-900">
                      {item.codigo ?? item.sigla}
                    </td>
                    <td className="py-2.5 px-3 text-slate-800">
                      <div className="font-medium text-slate-900 flex items-center gap-2">
                        <span>{item.nome}</span>
                        {destacado && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
                            <CheckCircle2 className="w-3 h-3" />
                            Novo
                          </span>
                        )}
                      </div>
                      {item.nomeFantasia ? (
                        <div className="text-[11px] text-slate-500 font-normal">{item.nomeFantasia}</div>
                      ) : null}
                      {item.documentoFiscal ? (
                        <div className="font-mono text-[10px] text-slate-400">
                          {formatarDocumentoFiscal(item.documentoFiscal)}
                        </div>
                      ) : null}
                    </td>
                    {activeTab === 'servicos' && (
                      <td className="py-2.5 px-3 text-center font-mono text-slate-600">
                        {item.ordemPadrao ?? 1}
                      </td>
                    )}
                    {activeTab === 'servicos' && (
                      <td className="py-2.5 px-3 text-center text-slate-600">
                        {item.apareceNoKanban ? 'Sim' : 'Não'}
                      </td>
                    )}
                    {activeTab === 'celulas' && (
                      <td className="py-2.5 px-3 text-slate-700">{item.servicoNome ?? 'Sem processo'}</td>
                    )}
                    {activeTab === 'celulas' && (
                      <td className="py-2.5 px-3 text-slate-700">{item.ehCabine ? 'Cabine' : '—'}</td>
                    )}
                    {activeTab === 'estufas' && (
                      <td className="py-2.5 px-3 text-slate-700">
                        {(item as EstufaEntity).cabines?.length > 0
                          ? (item as EstufaEntity).cabines.join(', ')
                          : 'Nenhuma cabine vinculada.'}
                      </td>
                    )}
                    {exibeFinanceiro && badgeFinanceiro && (
                      <td className="py-2.5 px-3 text-center">
                        {badgeFinanceiro.estado === 'bloqueado' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                            <Ban className="w-3 h-3 text-rose-600" />
                            {badgeFinanceiro.rotulo}
                          </span>
                        ) : badgeFinanceiro.estado === 'inadimplente' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                            <AlertTriangle className="w-3 h-3 text-amber-600" />
                            {badgeFinanceiro.rotulo}
                          </span>
                        ) : (
                          <span className="text-slate-300 text-[11px]">—</span>
                        )}
                      </td>
                    )}
                    {activeTab === 'clientes' &&
                      (() => {
                        const estado: EstadoIntegracaoOmie =
                          (item as ClienteEntity).estadoIntegracaoOmie ?? 'SOMENTE_LOCAL';
                        const IconeEstado = OMIE_ESTADO_ICON[estado];
                        return (
                          <td className="py-2.5 px-3 text-center">
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${OMIE_ESTADO_TOM[estado]}`}
                            >
                              <IconeEstado className="w-3 h-3" />
                              {ESTADO_INTEGRACAO_OMIE_LABEL[estado]}
                            </span>
                          </td>
                        );
                      })()}
                    <td className="py-2.5 px-3 text-center">
                      {item.ativo ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                          Ativo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-200">
                          <Slash className="w-3 h-3 text-slate-400" />
                          Inativo
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {activeTab === 'clientes' && podeGerenciarAcessosPortal && (
                          <a
                            href={`/cadastros/acessos-portal?clienteId=${encodeURIComponent(item.id)}`}
                            className="px-2 py-1 text-[11px] font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-100 rounded flex items-center gap-1"
                          >
                            <KeyRound className="w-3.5 h-3.5" />
                            Acessos do portal
                          </a>
                        )}
                        {activeTab === 'clientes' && (
                          <a
                            href={`/cadastros/clientes/${encodeURIComponent(item.id)}/editar`}
                            className="inline-flex min-h-11 items-center gap-1 rounded bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-200"
                          >
                            Editar
                          </a>
                        )}
                        {activeTab === 'clientes' && (
                          <button
                            type="button"
                            onClick={() => {
                              setClienteDetalhe(item as ClienteEntity);
                              setDetalheSheetOpen(true);
                            }}
                            className="px-2 py-1 text-[11px] font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded flex items-center gap-1"
                            title="Ver detalhes do cliente"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            Detalhes
                          </button>
                        )}

                        {/* D-07/D-08: envio tardio só nos estados elegíveis (SOMENTE_LOCAL/FALHA);
                            nunca em PENDENTE/SINCRONIZADO e nunca uma ação de desconexão. */}
                        {activeTab === 'clientes' &&
                          podeGerenciarCadastros &&
                          ((item as ClienteEntity).estadoIntegracaoOmie === 'SOMENTE_LOCAL' ||
                            (item as ClienteEntity).estadoIntegracaoOmie === 'FALHA_SINCRONIZACAO') && (
                            <button
                              type="button"
                              onClick={() => void handleEnviarOmie(item as ClienteEntity)}
                              disabled={enviandoOmieId === (item as ClienteEntity).id}
                              className="px-2 py-1 text-[11px] font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded flex items-center gap-1 disabled:opacity-50"
                            >
                              <UploadCloud className="w-3.5 h-3.5" />
                              {enviandoOmieId === (item as ClienteEntity).id ? 'Enviando…' : 'Enviar para a Omie'}
                            </button>
                          )}

                        {activeTab === 'celulas' && (
                          <button
                            type="button"
                            onClick={() => setCelulaKanbanModal(item as CelulaKanbanEntity)}
                            className="px-2 py-1 text-[11px] font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded flex items-center gap-1"
                            title="Processo, cabine de pintura e estufa no quadro Kanban"
                          >
                            <Layers className="w-3 h-3 text-slate-600" />
                            Kanban
                          </button>
                        )}

                        {activeTab === 'celulas' && (
                          <button
                            onClick={() =>
                              setCelulaTurnoModal({
                                id: item.id,
                                nome: item.nome,
                                codigo: item.codigo ?? '',
                              })
                            }
                            className="px-2 py-1 text-[11px] font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded flex items-center gap-1"
                            title="Gerenciar turnos e exceções de calendário"
                          >
                            <Clock className="w-3 h-3 text-slate-600" />
                            Turnos & Calendário
                          </button>
                        )}

                        {/* Celula nao tem PATCH generico (nome/codigo nao sao editaveis pela API):
                            a configuracao e pelo botao 'Kanban'. */}
                        {activeTab !== 'celulas' && (
                          <button
                            type="button"
                            onClick={() => {
                              setItemEdicao(item);
                              setAberturaFormulario((n) => n + 1);
                              setModalOpen(true);
                            }}
                            className="px-2 py-1 text-[11px] font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded flex items-center gap-1"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                            Editar
                          </button>
                        )}

                        {item.ativo && (
                          <button
                            type="button"
                            onClick={() => setItemDesativacao(item)}
                            className="px-2 py-1 text-[11px] font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100 rounded flex items-center gap-1"
                          >
                            <PowerOff className="w-3.5 h-3.5" />
                            Desativar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {!loading && itensFiltrados.length > 0 && (
          <TablePagination
            page={pagination.page}
            pageSize={pagination.pageSize}
            totalItems={itensFiltrados.length}
            itemLabel={currentMeta.plural.toLowerCase()}
            onPageChange={pagination.setPage}
            onPageSizeChange={pagination.setPageSize}
          />
        )}
      </div>

      {/* Modal de Formulário de Cadastro */}
      {modalOpen && activeTab === 'estufas' && (
        <EstufaForm
          key={aberturaFormulario}
          estufa={(itemEdicao as EstufaEntity | null) ?? null}
          podeEditar={podeGerenciarCadastros}
          onClose={() => {
            setModalOpen(false);
            setItemEdicao(null);
          }}
          onSalvo={() => {
            setModalOpen(false);
            setItemEdicao(null);
            void carregarItens();
          }}
        />
      )}
      {modalOpen && activeTab !== 'estufas' && (
        <CadastroForm
          key={aberturaFormulario}
          title={itemEdicao ? `Editar ${currentMeta.singular}` : rotulos.novo}
          fields={currentMeta.fields}
          initialValues={itemEdicao ?? {}}
          modo={itemEdicao ? 'editar' : 'criar'}
          onClose={() => {
            setModalOpen(false);
            setItemEdicao(null);
          }}
            onSave={handleSalvar}
        />
      )}

      {/* Modal de Confirmação de Desativação */}
      {itemDesativacao && (
        <CenteredDialog title="Desativar ?" onClose={() => setItemDesativacao(null)}>
          <div className="bg-white rounded-xl shadow-xl border w-full max-w-sm p-5 space-y-4 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center gap-3 text-rose-700">
              <AlertOctagon className="w-6 h-6 shrink-0 text-rose-600" />
              <h2 className="text-base font-bold text-slate-900">
                Desativar {itemDesativacao.nome}?
              </h2>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              Tem certeza que deseja desativar este cadastro? Registros históricos vinculados
              permanecerão intactos no sistema.
            </p>
            <div className="flex items-center justify-end gap-2 pt-2 border-t">
              <button
                onClick={() => setItemDesativacao(null)}
                className="px-3 py-1.5 border rounded-md text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                  onClick={() => void handleDesativar()}
                className="px-3 py-1.5 bg-rose-600 text-white rounded-md text-xs font-semibold hover:bg-rose-700"
              >
                Sim, Desativar
              </button>
            </div>
          </div>
        </CenteredDialog>
      )}

      {/* Modal de Turno e Calendário da Célula */}
      {celulaTurnoModal && (
        <CelulaTurnoCalendarioModal
          celulaId={celulaTurnoModal.id}
          celulaNome={celulaTurnoModal.nome}
          celulaCodigo={celulaTurnoModal.codigo}
          onClose={() => setCelulaTurnoModal(null)}
        />
      )}

      {/* Modal de configuração Kanban da Célula (processo, cabine e estufa) */}
      {celulaKanbanModal && (
        <CelulaKanbanForm
          celula={celulaKanbanModal}
          podeEditar={podeGerenciarCadastros}
          onClose={() => setCelulaKanbanModal(null)}
          onSalvo={() => {
            setCelulaKanbanModal(null);
            void carregarItens();
          }}
        />
      )}

      {/* Painel Lateral de Detalhes do Cliente (com Camadas Financeiras RBAC) */}
      <ClienteDetalheSheet
        cliente={clienteDetalhe}
        open={detalheSheetOpen}
        onOpenChange={setDetalheSheetOpen}
        session={sessao?.autenticado ? sessao.usuario : null}
      />
    </div>
  );
};
